/**
 * 總覽——「這個景點還缺什麼」一頁看完。
 *
 * ## 為什麼需要
 *
 * 編輯畫面的維度是三個：船上的畫面（一階／二階／推薦點）、語系（中英日）、以及
 * 內容種類（文字／圖片／語音／影片）。用頁籤表達的結果是 5 × 3 = 15 種畫面狀態，
 * 任何時刻只看得到十五分之一。於是「英文的二階語音沒掛」這種事，要嘛靠人逐一切
 * 過去確認，要嘛等發布到船上、乘客站在玻璃前才發現。
 *
 * 這一頁把三個維度壓成一張表：列是內容項、欄是語系，格子只回答「有沒有」。它不
 * 是新的編輯介面——點下去就跳到原本負責那一格的分頁，編輯行為完全沒變。
 *
 * ## 數字從哪裡來
 *
 * 全部來自這頁本來就有的三個查詢（景點表單、`spot.media`、`spot.pois`），沒有新
 * 端點。文字欄位讀的是**目前的表單狀態**而非伺服器值：使用者剛打完英文標題還沒按
 * 儲存時，把它標成「缺」比標成「有」更容易讓人以為自己沒填。表頭有註明這件事。
 *
 * 「發布前一定會擋下什麼」不在這裡回答——那是「產出預覽」分頁的工作，它連檔案
 * 清單與 §15 的回應形狀都算得出來。這頁只回答「還有哪幾格是空的」。
 */

import { useQuery } from '@tanstack/react-query';

import { Button } from './Button';
import { richTextIsEmpty } from './RichTextEditor';
import { toDisplayLocale } from './SpotMediaFields';
import { LOCALE_LABEL, LOCALES, type Locale, type SpotFormState } from './spot-form';
import { countPoiMtDrafts, poiCategoryHasMt } from './spot-poi-translation';
import { countMtDrafts } from './spot-translation';
import {
  listSpotMedia,
  listSpotPois,
  POI_CATEGORIES,
  POI_CATEGORY_LABEL,
  POI_MAX_SEQ,
  type SpotMediaList,
  type SpotMediaSlot,
  type SpotPoiList,
} from '../lib/api-client';

/** 格子的三種狀態；`warn` 是「有東西但不對」，與「沒東西」要分得出來。 */
type CellState = 'ok' | 'missing' | 'warn';

interface Cell {
  state: CellState;
  text: string;
}

interface Row {
  key: string;
  label: string;
  /** 點格子要跳去的分頁。 */
  tab: string;
  /** 這一列在船上對應的檔名，讓對接韌體的人也看得懂。 */
  output?: string;
  cell: (locale: Locale) => Cell;
  /** 整列共通的提醒（例如跨語系張數不一致）。 */
  warning?: string;
}

interface Section {
  title: string;
  rows: Row[];
}

const OK: Cell = { state: 'ok', text: '✓' };
const MISSING: Cell = { state: 'missing', text: '—' };

const CELL_STYLE: Record<CellState, React.CSSProperties> = {
  ok: { background: '#ecfdf5', color: '#047857' },
  missing: { background: '#fafafa', color: '#9ca3af' },
  warn: { background: '#fffbeb', color: '#b45309' },
};

interface Props {
  spotId: string;
  form: SpotFormState;
  /** 伺服器上的機翻待校記號：`欄位_i18n → 語系 → 'mt_draft'`。 */
  translationState: Record<string, Record<string, string>>;
  /** 點格子後跳到負責編輯它的分頁與語系。 */
  onJump: (tab: string, locale: Locale) => void;
  /** 一次解除所有機翻記號；沒有記號時呼叫端可不傳。 */
  onClearMt?: () => void;
  /** 解除中（避免重複點）。 */
  clearingMt?: boolean;
}

export function SpotContentMatrix({
  spotId,
  form,
  translationState,
  onJump,
  onClearMt,
  clearingMt,
}: Props): JSX.Element {
  const mediaQuery = useQuery({
    queryKey: ['spot.media', spotId],
    queryFn: () => listSpotMedia(spotId),
  });
  const poiQuery = useQuery({
    queryKey: ['spot.pois', spotId],
    queryFn: () => listSpotPois(spotId),
  });

  const sections = buildSections(form, mediaQuery.data, poiQuery.data, translationState);
  /*
   * 機翻的格數要把推薦點算進去。
   *
   * 兩邊的記號存在不同的表（`spots.translation_state` 與 `spot_pois.translation_state`），
   * 但對使用者來說是同一件事：「還有幾格沒人看過」。只算景點那一半的話，橫幅會在
   * 推薦點還掛著機翻時就消失。
   */
  const allPois = Object.values(poiQuery.data?.categories ?? {}).flat();
  const mtCount = countMtDrafts(translationState) + countPoiMtDrafts(allPois);
  const cells = sections.flatMap((s) => s.rows.flatMap((r) => LOCALES.map((l) => r.cell(l))));
  const filled = cells.filter((c) => c.state !== 'missing').length;

  return (
    <div style={{ margin: '12px 0' }}>
      {form.roi_id.trim() === '' && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
            padding: 8,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          ⚠ 此景點沒有窗屏編號（roi_id），<strong>不會被打包上船</strong>
          。內容填得再齊也不會出現在窗屏上——請到「基本資料」補上編號。
        </div>
      )}

      {mtCount > 0 && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
            padding: 8,
            fontSize: 12,
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span>
            ⚠ 有 <strong>{mtCount}</strong> 格是機器翻譯的草稿，還沒有人校對。
            改動該欄位的文字並儲存就會自動解除；若確認譯文可用而不需要改字，用右邊的按鈕。
          </span>
          {onClearMt && (
            <Button variant="secondary" onClick={onClearMt} disabled={clearingMt}>
              {clearingMt ? '處理中…' : '全部標記已校對'}
            </Button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>
          內容覆蓋率 {filled}／{cells.length}
        </strong>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          點任一格可跳到負責編輯它的分頁；文字欄位反映目前表單內容（含尚未儲存的編輯）
        </span>
      </div>

      {(mediaQuery.isLoading || poiQuery.isLoading) && <div style={{ fontSize: 13 }}>載入中…</div>}

      <div className="table-card" style={{ maxWidth: 720 }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: '46%' }}>內容項</th>
              {LOCALES.map((l) => (
                <th key={l} style={{ textAlign: 'center' }}>
                  {LOCALE_LABEL[l]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <SectionRows key={section.title} section={section} onJump={onJump} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionRows({
  section,
  onJump,
}: {
  section: Section;
  onJump: (tab: string, locale: Locale) => void;
}): JSX.Element {
  return (
    <>
      <tr>
        <td
          colSpan={1 + LOCALES.length}
          style={{ background: '#f9fafb', fontWeight: 600, fontSize: 12 }}
        >
          {section.title}
        </td>
      </tr>
      {section.rows.map((row) => (
        <tr key={row.key}>
          <td style={{ fontSize: 13 }}>
            {row.label}
            {row.output && (
              <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                {' '}
                → {row.output}
              </span>
            )}
            {row.warning && <div style={{ fontSize: 11, color: '#b45309' }}>⚠ {row.warning}</div>}
          </td>
          {LOCALES.map((locale) => {
            const cell = row.cell(locale);
            return (
              <td key={locale} style={{ textAlign: 'center', padding: 4 }}>
                <button
                  type="button"
                  onClick={() => onJump(row.tab, locale)}
                  title={`到「${row.label}」（${LOCALE_LABEL[locale]}）`}
                  style={{
                    ...CELL_STYLE[cell.state],
                    border: 'none',
                    borderRadius: 4,
                    padding: '4px 10px',
                    minWidth: 54,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {cell.text}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/**
 * 由三份資料組出整張表。
 *
 * 純函式（不碰 hook）：列的定義是這個元件唯一值得單獨看懂的部分，混在 JSX 裡會
 * 被樣式淹掉。
 */
function buildSections(
  form: SpotFormState,
  media: SpotMediaList | undefined,
  pois: SpotPoiList | undefined,
  translationState: Record<string, Record<string, string>>,
): Section[] {
  /** 有內容→綠勾；但還掛著機翻記號時要標黃，綠勾會讓人以為這格已經好了。 */
  const marked = (field: string, locale: Locale, filled: boolean): Cell => {
    if (!filled) return MISSING;
    return translationState[field]?.[locale] ? { state: 'warn', text: '機翻' } : OK;
  };

  const textCell =
    (field: keyof SpotFormState['display']) =>
    (locale: Locale): Cell =>
      marked(`${field}_i18n`, locale, (form.display[field]?.[locale] ?? '').trim() !== '');

  /** 景點名稱：純文字，與窗屏欄位同樣判斷。 */
  const nameCell = (locale: Locale): Cell =>
    marked('name_i18n', locale, (form.name[locale] ?? '').trim() !== '');

  /**
   * 景點描述是 RichTextEditor 產出的 HTML：清空之後留下的是 `<p></p>`，
   * 用 trim 判斷會把一個空白描述算成「已填」。
   */
  const descCell = (locale: Locale): Cell =>
    marked('description_i18n', locale, !richTextIsEmpty(form.description[locale] ?? ''));

  const entriesOf = (slot: SpotMediaSlot, locale: Locale): number =>
    (media?.slots?.[slot]?.[toDisplayLocale(locale)] ?? []).length;

  const singleCell =
    (slot: SpotMediaSlot) =>
    (locale: Locale): Cell =>
      entriesOf(slot, locale) > 0 ? OK : MISSING;

  const imageCell = (locale: Locale): Cell => {
    const n = entriesOf('poi_image', locale);
    if (n === 0) return MISSING;
    // 張數不一致是會讓部分語系缺圖的實際問題，不能只顯示綠勾
    return { state: media?.poi_image_consistent === false ? 'warn' : 'ok', text: `${n} 張` };
  };

  const poiCell =
    (category: (typeof POI_CATEGORIES)[number]) =>
    (locale: Locale): Cell => {
      const list = pois?.categories?.[category] ?? [];
      // CMS 的語系代碼本身就是 i18n 物件的鍵（zh-TW / en / ja），不需要再映射一次
      const n = list.filter((p) => (p.name_i18n?.[locale] ?? '').trim() !== '').length;
      if (n === 0) return MISSING;
      // 機翻與「筆數不足」都是黃的，但要講得出是哪一種——兩者的下一步完全不同
      if (poiCategoryHasMt(list, locale)) {
        return { state: 'warn', text: `${n}／${POI_MAX_SEQ} 機翻` };
      }
      return { state: n < POI_MAX_SEQ ? 'warn' : 'ok', text: `${n}／${POI_MAX_SEQ}` };
    };

  return [
    {
      title: '基本資料（網站、購票機與 PWA 用；不會送上船）',
      rows: [
        { key: 'name', label: '景點名稱', tab: 'basic', cell: nameCell },
        { key: 'description', label: '景點描述', tab: 'basic', cell: descCell },
      ],
    },
    {
      title: '一階畫面（船靠近時跳出的卡片）',
      rows: [
        { key: 'location_name', label: '地標名稱', tab: 'level1', cell: textCell('location_name') },
        { key: 'poi_title', label: '一階標題', tab: 'level1', cell: textCell('poi_title') },
        { key: 'poi_desc', label: '一階描述', tab: 'level1', cell: textCell('poi_desc') },
        { key: 'intro', label: '景點介紹', tab: 'level1', cell: textCell('intro') },
        {
          key: 'poi_image',
          label: '一階圖片',
          tab: 'level1',
          output: '1_pic{n}.png',
          cell: imageCell,
          ...(media?.poi_image_consistent === false
            ? { warning: '各語系張數不一致，roiN.txt 只有一份清單，部分語系會缺圖' }
            : {}),
        },
        {
          key: 'intro_voice',
          label: '一階語音',
          tab: 'level1',
          output: '1_voice1.wav',
          cell: singleCell('intro_voice'),
        },
      ],
    },
    {
      title: '二階畫面（點進去的詳細頁）',
      rows: [
        { key: 'detail_title', label: '二階標題', tab: 'level2', cell: textCell('detail_title') },
        { key: 'detail_text', label: '二階內文', tab: 'level2', cell: textCell('detail_text') },
        {
          key: 'detail_video',
          label: '二階影片',
          tab: 'level2',
          output: '2_video1.mp4',
          cell: singleCell('detail_video'),
        },
        {
          key: 'detail_voice',
          label: '二階語音',
          tab: 'level2',
          output: '2_voice1.wav',
          cell: singleCell('detail_voice'),
        },
      ],
    },
    {
      title: `推薦點（每個分類最多 ${POI_MAX_SEQ} 筆，以店名是否填寫計算）`,
      rows: POI_CATEGORIES.map((c) => ({
        key: `poi-${c}`,
        label: POI_CATEGORY_LABEL[c],
        tab: 'pois',
        cell: poiCell(c),
      })),
    },
  ];
}
